import * as React from 'react'
import {Box, Typography, Button, Alert} from '@mui/material'
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import {alertStripSx, secondaryBtnSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, INFO, WARNING, SUCCESS} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'

import {int} from '../format'
import {RULES} from '../data/reference'
import {POLICY} from '../data/sopCopy'
import {subcategoryRows, observedBaskets} from './state/packingModel'
import SubcategoryPanel from './SubcategoryPanel'
import PackingPanel from './PackingPanel'
import BaseDataPanel from './BaseDataPanel'
import BasketCalculator from './BasketCalculator'

const policyAlertSx = {fontFamily: FONT, fontSize: SIZE.xs, py: 0.85, '& .MuiAlert-message': {lineHeight: 1.6}}

const HEADER_COPY = {
  subcat: {
    title: 'Sub-categories',
    body: 'The group a freeze is agreed against. Dead weight belongs to the product, so the '
      + 'sub-category is what a product-level freeze can actually cover.',
  },
  packing: {
    title: 'Packing material',
    body: 'The material the shipment goes out in. Volumetric weight belongs to the box, so the '
      + 'packing type is what decides the charged weight on a light product.',
  },
}

/**
 * The two figures for whichever half is on screen, as stat cards for the strip
 * Package master renders ABOVE its tab bar (see freeze-v2/index.js). They used
 * to be a tile row inside this page; the strip is now the sub-module's header,
 * so the row moved up and only the data comes from here.
 *
 * A builder rather than a component: the container needs the numbers, not
 * markup, and the derivation stays in `state/packingModel.js` — `subcategoryRows`
 * and `observedBaskets`, exactly as before, each computed only for the half that
 * shows it.
 *
 * Each card's sentence is split across `label` and `note` the way Catalogue's
 * are: the strip prints the label in uppercase, which a full clause reads badly
 * in, so the clause moves to the note underneath and the label stays the two or
 * three words the figure is called.
 */
export function packingStatCards(state, part) {
  if (part === 'subcat') {
    const rows = subcategoryRows(state)
    const categories = new Set(rows.map((r) => r.cat)).size
    const loose = state.skus.filter((s) => !s.sub || s.sub === 'Uncategorised').length
    return [
      {id: 'subcats', value: rows.length, label: 'Sub-categories', note: `Across ${int(categories)} categories`, color: INFO},
      {
        id: 'loose',
        value: loose,
        label: 'Uncategorised SKUs',
        note: loose
          ? `Of ${int(state.skus.length)} SKUs. A sub-category freeze cannot reach them`
          : `All ${int(state.skus.length)} SKUs sit in a named sub-category`,
        color: loose ? WARNING : SUCCESS,
      },
    ]
  }

  const baskets = observedBaskets(state).length
  const packs = state.packs
  return [
    {
      id: 'packs',
      value: packs.length,
      label: 'Packing materials',
      note: `Brands typically carry ${RULES.PACKING_MATERIAL_BAND[0]} to ${RULES.PACKING_MATERIAL_BAND[1]} types`,
      color: packs.length > RULES.PACKING_MATERIAL_BAND[1]
        ? WARNING
        : packs.length < RULES.PACKING_MATERIAL_BAND[0] ? INFO : SUCCESS,
    },
    {
      id: 'baskets',
      value: baskets,
      label: 'Observed baskets',
      note: 'Multi-SKU baskets a computed weight can be checked against',
      color: SUCCESS,
    },
  ]
}

/**
 * Packing — two of Package master's three leaves (Catalogue, Sub-categories,
 * Packing material): the sub-category taxonomy (`part='subcat'`,
 * this task's SubcategoryPanel below) and the packing-material list
 * (`part='packing'`, PackingPanel/BaseDataPanel/BasketCalculator). Both
 * parts live in one file/component because they share the same per-SKU
 * dead/volumetric/chargeable split, via `state/packingModel.js` — `part`
 * only chooses which half mounts, it does not fork the model.
 *
 * `props` is whatever `index.js` threads down from the ONE module-wide
 * `useFreezeStore()` instance, held above the section switch — see that
 * hook's own header for why. This page does not call a second store hook
 * and does not hold any of skus/subcats/packs/requests in a local
 * `useState`: index.js unmounts the active section on every rail click, so
 * section-local state would vanish on navigation, and Packing's own
 * cascade (a pack edit rewriting `chargeKg`) has to stay visible to
 * Catalogue and Analytics, which read the exact same arrays.
 *
 * `state` below is just a narrow, freshly-assembled view over those props —
 * the slice `subcategoryRows`/`observedBaskets` and the panels beneath this
 * page actually read — never a second copy held across renders. Compare
 * `catalogue/useCatalogueState.js`'s own `state: {skus, mapLog}` for the
 * same pattern one section over.
 */
export default function PackingPage(props) {
  const {skus, combos, packs, subcats, requests, actions, onNavigate, focusFallbackRef, part = 'subcat'} = props
  const state = {skus, combos, packs, subcats, requests}
  const header = HEADER_COPY[part]

  return (
    <Box sx={{display: 'grid', gap: GAP.lg}}>
      {/* Header */}
      <Box sx={{display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, justifyContent: 'space-between', alignItems: {xs: 'flex-start', sm: 'center'}, gap: 1.5}}>
        <Box>
          {/* Type parity with disputes-v2 (zoom: 0.8 shell): SIZE.md is the one
              in-content heading tier, SIZE['2xs'] the one subtitle tier. */}
          <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY}}>
            {header.title}
          </Typography>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.25, maxWidth: 640}}>
            {header.body}
          </Typography>
        </Box>
        <Button
          sx={secondaryBtnSx}
          startIcon={<AcUnitOutlinedIcon sx={{fontSize: 16}} />}
          onClick={() => onNavigate('initiate')}
        >
          Initiate a weight freeze
        </Button>
      </Box>

      {/* Policy note — POLICY.subcatDefault belongs to Packing, not SoP (see
          data/sopCopy.js's own comment on SUBCAT_DEFAULT, and the note on
          SopPage.js deliberately NOT rendering it). Verbatim, non-dismissible
          — the same Alert is also rendered at the top of SubcategoryDrawer,
          in all three of its modes. It is a sub-category policy, so it
          renders only on the subcat part, not on packing. */}
      {part === 'subcat' && <Alert severity='info' sx={policyAlertSx}>{POLICY.subcatDefault}</Alert>}

      {/* Non-persistence — stated in the UI, not only in a code comment.
          Same treatment CataloguePage gives its own equivalent notice, so
          the two sections read as one product. */}
      <Box sx={{...alertStripSx(INFO), alignItems: 'flex-start'}}>
        <InfoOutlinedIcon sx={{fontSize: 16, mt: '2px'}} />
        <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, lineHeight: 1.6, fontWeight: WEIGHT.regular}}>
          Every change on this screen is held in the browser for this session only. There is no save to Prozo
          behind it yet, so a reload puts the account back the way it was.
        </Typography>
      </Box>

      {part === 'subcat'
        ? <SubcategoryPanel state={state} actions={actions} focusFallbackRef={focusFallbackRef} />
        : (
          <>
            {/* The packing-material panel — Task 6. */}
            <PackingPanel state={state} actions={actions} focusFallbackRef={focusFallbackRef} />

            {/* The base-data grid — Task 7: the per-SKU dead/volumetric/chargeable
                split. */}
            <BaseDataPanel state={state} actions={actions} />

            {/* The multi-SKU basket calculator — Task 8, the mockup's own "point
                of the screen". Read-only against the store (no `actions`): it
                raises no request and edits nothing here, only computes. */}
            <BasketCalculator state={state} onNavigate={onNavigate} />
          </>
        )}
    </Box>
  )
}
